import React, {useState, useEffect, useCallback, useContext} from 'react';
import {
  Alert,
  Button,
  CircularProgress,
  Grid,
  SelectChangeEvent,
  useMediaQuery,
} from '@mui/material';
import {SidePanelContent, SidePanelFooter} from '@securspace/securspace-ui-kit';
import SimpleSelect from '../../SimpleSelect';
import {requestCheckInFieldsForAccountId} from '../request/check-in-requests';
import {correlationIdRequest} from '../request/correlationId-request';
import {validateCustomCheckInConfiguration} from '../util/check-in-validation-util';
import {AssetType, AssetTypeLabel} from '../../constants/securspace-constants';
import {AppContext} from '../../../context/app-context';
import {CheckInFormProps, FormState, FormErrors, EMPTY_FORM} from './interfaces';
import {formRegistry} from './forms/formRegistry';
import {useCheckInImageUpload} from '../../../hooks/checkin/useCheckInImageUpload';
import ImageUploadSection from './fields/ImageUploadSection';

// ─── Constants ────────────────────────────────────────────────────────────────

/**
 * Asset types currently supported in the check-in form.
 * Derived from constants to avoid duplication.
 * Note: Excludes `TRUCK_CONTAINER_LOADED`, `TRUCK_CONTAINER_EMPTY`, `REEFER` variants
 * such as `REEFER_LOADED_NO_PLUGIN` and `TRUCK_REEFER_LOADED_PLUGIN`
 * which are not yet enabled in the UI, and `AssetType.ALL` which is a special filter.
 */
const ACTIVE_ASSET_TYPES = [
  AssetType.CONTAINER_LOADED,
  AssetType.CONTAINER_EMPTY,
  AssetType.CHASSIS_ONLY,
  AssetType.TRUCK_TRAILER_LOADED,
  AssetType.TRUCK_TRAILER_EMPTY,
  AssetType.TRUCK_ONLY,
  AssetType.TRUCK_ONLY_25,
  AssetType.TRAILER_LOADED,
  AssetType.TRAILER_EMPTY,
  AssetType.REEFER_LOADED_PLUGIN,
];

const ASSET_TYPE_OPTIONS = ACTIVE_ASSET_TYPES.map(key => ({value: key, label: AssetTypeLabel[key]}));

/**
 * Check-in form orchestrator rendered inside the SidePanel.
 * Manages asset type selection, prerequisites, dirty tracking, and submission.
 * Delegates field rendering to per-asset-type form components via formRegistry.
 */
const CheckInForm = ({onFormSave, onCancel, onDirtyChange, loading}: CheckInFormProps) => {
  const appContext = useContext(AppContext);
  const accountId = appContext.user?.id;
  const isTouch = useMediaQuery('(pointer: coarse)');

  // ── Prerequisites ──────────────────────────────────────────────────────────
  const [correlationId, setCorrelationId] = useState<string | null>(null);
  const [fieldMap, setFieldMap] = useState<any[] | null>(null);
  const [prerequisitesLoading, setPrerequisitesLoading] = useState(true);

  // ── Form state ─────────────────────────────────────────────────────────────
  const [assetType, setAssetType] = useState<string>('');
  const [form, setForm] = useState<FormState>(EMPTY_FORM);
  const [errors, setErrors] = useState<FormErrors>({});
  const {stagedFiles, addFiles, removeFile, replaceFile, clearFiles} = useCheckInImageUpload();


  // ── Prerequisites fetch ────────────────────────────────────────────────────
  useEffect(() => {
    if (!accountId) return;
    let cancelled = false;
    setPrerequisitesLoading(true);

    Promise.allSettled([
      requestCheckInFieldsForAccountId(accountId),
      correlationIdRequest(),
    ]).then(([checkInFieldsResponse, correlationIdResponse]) => {
      if (cancelled) return;

      if (checkInFieldsResponse.status === 'fulfilled') {
        setFieldMap(checkInFieldsResponse.value?.body ?? null);
      } else {
        // Non-fatal: form falls back to per-asset-type validation without custom fields
        if (process.env.NODE_ENV === 'development') {
          console.warn('[CheckInForm] Failed to load custom check-in fields:', checkInFieldsResponse.reason);
        }
        setFieldMap(null);
      }

      if (correlationIdResponse.status === 'fulfilled') {
        setCorrelationId(correlationIdResponse.value?.body ?? null);
      } else {
        // Non-fatal: correlationId is optional in the submission payload
        if (process.env.NODE_ENV === 'development') {
          console.warn('[CheckInForm] Failed to load correlation ID:', correlationIdResponse.reason);
        }
        setCorrelationId(null);
      }

      setPrerequisitesLoading(false);
    });

    return () => {
      cancelled = true;
    };
  }, [accountId]);

  // ── Dirty tracking ─────────────────────────────────────────────────────────
  useEffect(() => {
    const hasValues =
      assetType !== '' ||
      Object.values(form).some((v) => v !== '') ||
      stagedFiles.length > 0;
    onDirtyChange(hasValues);
  }, [assetType, form, stagedFiles.length, onDirtyChange]);

  // ── Asset type change → reset form fields ──────────────────────────────────
  const handleAssetTypeChange = useCallback((event: SelectChangeEvent) => {
    setAssetType(event.target.value);
    setForm(EMPTY_FORM);
    setErrors({});
    clearFiles();
  }, [clearFiles]);

  // ── Generic value change (called by field components) ─────────────────────
  // Field components apply their own transformations (uppercase, space-strip,
  // sanitize) before calling this. The orchestrator simply stores the value.
  const handleValueChange = useCallback((name: string, value: string) => {
    setForm((prev) => ({...prev, [name]: value}));
    setErrors((prev) => ({...prev, [name]: undefined, submit: undefined}));
  }, []);

  // ── Asset size select ─────────────────────────────────────────────────────
  const handleAssetSizeChange = useCallback((event: SelectChangeEvent) => {
    setForm((prev) => ({...prev, assetSize: event.target.value}));
    setErrors((prev) => ({...prev, assetSize: undefined, submit: undefined}));
  }, []);

  // ── Submission ─────────────────────────────────────────────────────────────
  const handleSubmit = useCallback(() => {
    if (!assetType) {
      setErrors({submit: 'Please select an equipment type.'});
      return;
    }

    setErrors({});

    const values = {...form, assetType};
    let errorMessage: string | undefined;

    // Custom-field override: when the account has custom check-in fields
    // configured, use the custom validator instead of the per-form validator.
    if (fieldMap && fieldMap.length > 0) {
      errorMessage = validateCustomCheckInConfiguration(values, fieldMap) ?? undefined;
    } else {
      const config = formRegistry[assetType];
      errorMessage = config?.validate(values);
    }

    if (errorMessage) {
      setErrors({submit: errorMessage});
      return;
    }

    onFormSave({
      containerNumber: form.containerNumber.trim(),
      chassisNumber: form.chassisNumber.trim(),
      chassisLicensePlateNumber: form.chassisLicensePlateNumber.trim(),
      sealNumber: form.sealNumber.trim(),
      trailerNumber: form.trailerNumber.trim(),
      driverFirstName: form.driverFirstName.trim(),
      driverLastName: form.driverLastName.trim(),
      driverLicenseNumber: form.driverLicenseNumber.trim(),
      truckLicensePlateNumber: form.truckLicensePlateNumber.trim(),
      scacCode: form.scacCode.trim(),
      assetSize: form.assetSize.trim(),
      notes: form.notes.trim(),
      assetType,
      correlationId,
      stagedFiles,
    });
  }, [assetType, form, fieldMap, correlationId, stagedFiles, onFormSave]);

  // ─── Loading / Error guards ────────────────────────────────────────────────

  if (prerequisitesLoading) {
    return (
      <SidePanelContent aria-busy="true">
        <Grid container justifyContent="center" p="2rem">
          <CircularProgress/>
        </Grid>
      </SidePanelContent>
    );
  }

  // ─── Resolve the per-asset-type form component ─────────────────────────────
  const formConfig = assetType ? formRegistry[assetType] : undefined;
  const AssetTypeForm = formConfig?.component;

  // ─── Render ────────────────────────────────────────────────────────────────
  return (
    <>
      <SidePanelContent>
        <Grid
          component="form"
          id="checkin-form"
          onSubmit={(e) => e.preventDefault()}
          container
          direction="column"
          gap={2}
        >
          {/* Asset Type Selector */}
          <SimpleSelect
            id="assetType"
            name="assetType"
            label="Asset Type"
            onChange={handleAssetTypeChange}
            value={assetType}
            placeholder="Select the equipment type being checked in"
            displayEmpty
            options={ASSET_TYPE_OPTIONS}
            native={isTouch}
          />

          {/* Per-asset-type form fields */}
          {AssetTypeForm && (
            <>
              <AssetTypeForm
                form={form}
                errors={errors}
                onValueChange={handleValueChange}
                onAssetSizeChange={handleAssetSizeChange}
              />

              <ImageUploadSection
                files={stagedFiles}
                onFilesAdded={addFiles}
                onFileRemoved={removeFile}
                onFileReplaced={replaceFile}
              />

              {/* Submit error message */}
              {errors.submit && (
                <Alert severity="error">{errors.submit}</Alert>
              )}
            </>
          )}
        </Grid>
      </SidePanelContent>

      <SidePanelFooter>
        <Button
          fullWidth
          variant="outlined"
          onClick={onCancel}
          disabled={loading}
          aria-label="Cancel Check In"
        >
          Cancel
        </Button>
        <Button
          type="submit"
          form="checkin-form"
          fullWidth
          variant="contained"
          color="primary"
          onClick={handleSubmit}
          disabled={loading}
          startIcon={loading ? <CircularProgress size={16} color="inherit"/> : undefined}
          sx={{mt: 2}}
        >
          {loading ? 'Checking In…' : 'Check In'}
        </Button>
      </SidePanelFooter>
    </>
  );
};

export default CheckInForm;

